/**
 * Peak Saunas - Full Attribution Tracker v1.0
 * 
 * Captures first-touch and last-touch attribution for every order.
 * Stores in localStorage and passes to Shopify cart attributes.
 * 
 * Data captured:
 * - first_touch_source, first_touch_medium, first_touch_campaign, first_touch_content, first_touch_term
 * - first_touch_date, first_touch_landing_page
 * - last_touch_source, last_touch_medium, last_touch_campaign, last_touch_content, last_touch_term
 * - last_touch_date, last_touch_landing
 * - gclid, fbclid, msclkid (click IDs for platform matching)
 */

(function() {
  'use strict';
  
  const STORAGE_KEY = 'peak_attribution';
  const DEBUG = false; // Set to true for console logging
  
  function log(...args) {
    if (DEBUG) console.log('[Attribution]', ...args);
  }
  
  // Get URL parameters
  function getUrlParams() {
    const params = {};
    const searchParams = new URLSearchParams(window.location.search);
    
    // UTM params
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    
    // Click IDs
    ['gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'].forEach(key => {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    
    return params;
  }
  
  // Infer source from click IDs or referrer
  function inferSource(params) {
    if (params.gclid) {
      return { source: 'google', medium: 'cpc', inferred: 'google_ads' };
    }
    if (params.fbclid) {
      return { source: 'facebook', medium: 'paid_social', inferred: 'meta_ads' };
    }
    if (params.msclkid) {
      return { source: 'bing', medium: 'cpc', inferred: 'microsoft_ads' };
    }
    if (params.ttclid) {
      return { source: 'tiktok', medium: 'paid_social', inferred: 'tiktok_ads' };
    }
    if (params.li_fat_id) {
      return { source: 'linkedin', medium: 'paid_social', inferred: 'linkedin_ads' };
    }
    
    // Check referrer
    const referrer = document.referrer.toLowerCase();
    if (referrer) {
      if (referrer.includes('google.')) {
        return { source: 'google', medium: 'organic', inferred: 'google_organic' };
      }
      if (referrer.includes('facebook.') || referrer.includes('fb.')) {
        return { source: 'facebook', medium: 'social', inferred: 'meta_organic' };
      }
      if (referrer.includes('instagram.')) {
        return { source: 'instagram', medium: 'social', inferred: 'meta_organic' };
      }
      if (referrer.includes('bing.')) {
        return { source: 'bing', medium: 'organic', inferred: 'bing_organic' };
      }
      if (referrer.includes('yahoo.')) {
        return { source: 'yahoo', medium: 'organic', inferred: 'yahoo_organic' };
      }
      if (referrer.includes('duckduckgo.')) {
        return { source: 'duckduckgo', medium: 'organic', inferred: 'duckduckgo_organic' };
      }
      if (referrer.includes('perplexity.')) {
        return { source: 'perplexity', medium: 'ai', inferred: 'ai_search' };
      }
      if (referrer.includes('chatgpt.') || referrer.includes('openai.')) {
        return { source: 'chatgpt', medium: 'ai', inferred: 'ai_search' };
      }
      if (referrer.includes('youtube.')) {
        return { source: 'youtube', medium: 'social', inferred: 'youtube' };
      }
      if (referrer.includes('reddit.')) {
        return { source: 'reddit', medium: 'social', inferred: 'reddit' };
      }
      if (referrer.includes('twitter.') || referrer.includes('x.com')) {
        return { source: 'twitter', medium: 'social', inferred: 'twitter' };
      }
      if (!referrer.includes('peaksaunas.com')) {
        // External referrer
        try {
          const domain = new URL(referrer).hostname.replace('www.', '');
          return { source: domain, medium: 'referral', inferred: 'referral' };
        } catch (e) {}
      }
    }
    
    // Direct traffic
    return { source: 'direct', medium: 'none', inferred: 'direct' };
  }
  
  // Load stored attribution
  function loadAttribution() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }
  
  // Save attribution
  function saveAttribution(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      log('Saved attribution:', data);
    } catch (e) {
      log('Failed to save:', e);
    }
  }
  
  // Process current visit
  function processVisit() {
    const urlParams = getUrlParams();
    const inferred = inferSource(urlParams);
    const now = new Date().toISOString();
    const landingPage = window.location.pathname;
    
    // Build current touch data
    const currentTouch = {
      source: urlParams.utm_source || inferred.source,
      medium: urlParams.utm_medium || inferred.medium,
      campaign: urlParams.utm_campaign || '',
      content: urlParams.utm_content || '',
      term: urlParams.utm_term || '',
      inferred_source: inferred.inferred,
      date: now,
      gclid: urlParams.gclid || '',
      fbclid: urlParams.fbclid || '',
      msclkid: urlParams.msclkid || ''
    };
    
    // Load existing attribution
    let attribution = loadAttribution();
    
    if (!attribution) {
      // First visit - set both first and last touch
      attribution = {
        first_touch: { ...currentTouch, landing_page: landingPage },
        last_touch: { ...currentTouch, landing_page: landingPage },
        visits: 1,
        created: now
      };
      log('First visit, setting first touch:', attribution);
    } else {
      // Return visit - only update last touch if there's meaningful attribution
      const hasNewAttribution = 
        urlParams.utm_source || 
        urlParams.gclid || 
        urlParams.fbclid || 
        urlParams.msclkid ||
        (document.referrer && !document.referrer.includes('peaksaunas.com'));
      
      if (hasNewAttribution) {
        attribution.last_touch = { ...currentTouch, landing_page: landingPage };
        log('Return visit with new attribution, updating last touch');
      }
      
      attribution.visits = (attribution.visits || 0) + 1;
      attribution.updated = now;
    }
    
    saveAttribution(attribution);
    return attribution;
  }
  
  // Update cart with attribution
  async function updateCartAttributes(attribution) {
    if (!attribution) return;
    
    const attributes = {
      // First touch
      first_touch_source: attribution.first_touch.source || '',
      first_touch_medium: attribution.first_touch.medium || '',
      first_touch_campaign: attribution.first_touch.campaign || '',
      first_touch_content: attribution.first_touch.content || '',
      first_touch_term: attribution.first_touch.term || '',
      first_touch_inferred: attribution.first_touch.inferred_source || '',
      first_touch_date: attribution.first_touch.date || '',
      first_touch_landing: attribution.first_touch.landing_page || '',
      
      // Last touch
      last_touch_source: attribution.last_touch.source || '',
      last_touch_medium: attribution.last_touch.medium || '',
      last_touch_campaign: attribution.last_touch.campaign || '',
      last_touch_content: attribution.last_touch.content || '',
      last_touch_term: attribution.last_touch.term || '',
      last_touch_inferred: attribution.last_touch.inferred_source || '',
      last_touch_date: attribution.last_touch.date || '',
      // last_touch_landing was missing: the last-touch object never carried landing_page, so
      // Shopify showed a first-touch landing page and nothing for last touch.
      last_touch_landing: attribution.last_touch.landing_page || '',
      
      // Click IDs (for deduplication with ad platforms)
      attribution_gclid: attribution.first_touch.gclid || attribution.last_touch.gclid || '',
      attribution_fbclid: attribution.first_touch.fbclid || attribution.last_touch.fbclid || '',
      attribution_msclkid: attribution.first_touch.msclkid || attribution.last_touch.msclkid || '',
      
      // Meta
      attribution_visits: String(attribution.visits || 1),
      attribution_version: 'v1.0'
    };
    
    // Filter out empty values
    const filteredAttributes = {};
    Object.entries(attributes).forEach(([key, value]) => {
      if (value) filteredAttributes[key] = value;
    });
    
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: filteredAttributes })
      });
      
      if (response.ok) {
        log('Cart attributes updated:', filteredAttributes);
      }
    } catch (e) {
      log('Failed to update cart:', e);
    }
  }
  
  // Intercept add to cart to ensure attribution is set
  function interceptAddToCart() {
    // Intercept fetch for add to cart
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const [url] = args;
      
      if (typeof url === 'string' && url.includes('/cart/add')) {
        const attribution = loadAttribution();
        if (attribution) {
          await updateCartAttributes(attribution);
        }
      }
      
      return originalFetch.apply(this, args);
    };
    
    // Also intercept form submissions for traditional add to cart
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        const attribution = loadAttribution();
        if (attribution) {
          await updateCartAttributes(attribution);
        }
      }
    }, true);
  }
  
  // Update cart on every page load to ensure attribution is always current
  // Critical for Buy Now / direct checkout flows that bypass the cart page
  function setupCheckoutTracking() {
    const attribution = loadAttribution();
    if (!attribution) return;

    // Always update cart attributes on every page (keeps in sync as customer browses)
    updateCartAttributes(attribution);

    // Extra: inject into checkout note_attributes via Shopify checkout object
    const path = window.location.pathname;
    if (path.includes('/checkout')) {
      // Retry cart update a few times to beat any race conditions
      setTimeout(() => updateCartAttributes(attribution), 500);
      setTimeout(() => updateCartAttributes(attribution), 1500);
      setTimeout(() => updateCartAttributes(attribution), 3000);
    }
  }

  // Parse fbclid/gclid from URL and upgrade existing "direct" first-touch if present
  // Fixes: customer had a prior direct visit saved, then came back via paid click
  function upgradeAttributionFromUrl() {
    const params = getUrlParams();
    const hasClickId = params.gclid || params.fbclid || params.msclkid || params.ttclid;
    if (!hasClickId) return;

    const attribution = loadAttribution();
    if (!attribution) return;

    // If first touch was stored as direct but this visit has a click ID, upgrade it
    if (attribution.first_touch && attribution.first_touch.source === 'direct') {
      const inferred = inferSource(params);
      attribution.first_touch = {
        ...attribution.first_touch,
        source: inferred.source,
        medium: inferred.medium,
        inferred_source: inferred.inferred,
        gclid: params.gclid || '',
        fbclid: params.fbclid || '',
        msclkid: params.msclkid || '',
      };
      saveAttribution(attribution);
      log('Upgraded first touch from direct to:', inferred.source);
    }
  }
  
  // Initialize
  function init() {
    log('Initializing Full Attribution Tracker v1.0');
    
    // Process this visit
    const attribution = processVisit();

    // Upgrade direct → paid if click ID present on this URL
    upgradeAttributionFromUrl();
    
    // Set up cart interception
    interceptAddToCart();
    
    // Update cart on every page load (critical for Buy Now / direct checkout)
    setupCheckoutTracking();
    
    // Expose for debugging
    window.PeakAttribution = {
      get: loadAttribution,
      process: processVisit,
      updateCart: () => updateCartAttributes(loadAttribution()),
      clear: () => localStorage.removeItem(STORAGE_KEY),
      debug: () => {
        const data = loadAttribution();
        console.log('=== Peak Attribution Data ===');
        console.log(JSON.stringify(data, null, 2));
        return data;
      }
    };
    
    log('Attribution tracker initialized');
  }
  
  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
